import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchWithHeaders } from '@sendible/common';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { normalisePlatform, transformApiModule, transformApiTemplate } from './transforms';
import { useQueryWithAccessToken, useMutationWithAccessToken, useInfiniteQueryWithAccessToken } from '../../data-layer/useReactQueryWithAccessToken';
import { MutationMethodEnum } from '../../data-layer/types';
import { ReportingUrls } from '../../constants/urls';

export { useModuleMetadata } from './useModuleMetadata';
export { getModuleTranslationKeys, MODULE_CODE_TO_TRANSLATION_KEYS } from './transforms';

// ── Stale time constants ──

const STALE_TIME_MODULE_DEFINITIONS = 30 * 60 * 1000; // 30 min — catalogue rarely changes
const STALE_TIME_MODULE_METRICS = 5 * 60 * 1000; // 5 min

// ── Query key helpers ──

const queryKeys = {
  profiles: ReportingUrls.profiles,
  reports: ReportingUrls.reports,
  templates: ReportingUrls.templates,
  moduleDefinitions: ReportingUrls.moduleDefinitions,
} as const;

function unwrapResult<TResult>(response: unknown): TResult {
  if (typeof response === 'object' && response !== null && 'result' in response) {
    return (response as { result: TResult }).result;
  }

  return response as TResult;
}

// ── Profiles (connectable social profiles) ──
// The v1.0/filters endpoint returns all networks.
// Profile IDs use the metadata.profileId format, which maps to report profileIds.

export const useGetProfiles = () => {
  const [{ user }] = useBridgeContext();
  const { userId } = user || {};
  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);

  const endpoint = isWorkspaceMode ? ReportingUrls.profiles.replace('/v1.0/', '/v2.0/') : ReportingUrls.profiles;

  const dashboardParams = !isWorkspaceMode && userId ? { dashboardId: userId } : {};
  const workspaceParams = isWorkspaceMode && userId ? { workspaceId: userId } : {};

  const query = useQueryWithAccessToken<FilterData>(
    [endpoint, { filterTypes: 'Profile', ...dashboardParams, ...workspaceParams }],
    {
      staleTime: Infinity,
      cacheTime: 0,
    },
    false
  );

  const normalised = useMemo((): ReportProfile[] => {
    const items = query.data?.profiles ?? [];

    return items.map((p) => {
      const platform = normalisePlatform(p.networks?.[0] ?? '');

      return {
        id: String(p.metadata?.profileId ?? ''),
        name: p.label ?? '',
        platform,
        status: ((p.metadata?.status as string) ?? 'Ready') as ProfileStatus,
        avatar: p.icon,
      };
    });
  }, [query.data]);

  return { ...query, data: normalised };
};

// ── Module definitions (catalogue) ──

export const useGetModuleDefinitions = (category?: string) =>
  useQueryWithAccessToken<ModuleDefinition[]>(
    [queryKeys.moduleDefinitions, category ? { category } : {}],
    {
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME_MODULE_DEFINITIONS,
    },
    false
  );

export const useGetModuleDefinition = (id: string | null) =>
  useQueryWithAccessToken<ModuleDefinition>(
    [id ? ReportingUrls.moduleDefinition(id) : '', {}],
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
    },
    false
  );

// ── Report list ──

export const useGetReports = (workspaceId?: number, name?: string) =>
  useQueryWithAccessToken<ApiReport[]>(
    [queryKeys.reports, { ...(workspaceId ? { workspaceId } : {}), ...(name ? { name } : {}) }],
    {
      refetchOnWindowFocus: false,
    },
    false
  );

// ── Single report ──
// The API returns an ApiReport; we transform modules from the API shape
// into frontend ReportModule using the module definitions catalogue.

export const useGetReport = (reportId: string | null) => {
  const { data: definitions } = useGetModuleDefinitions();

  const query = useQueryWithAccessToken<ApiReport>(
    [reportId ? ReportingUrls.report(reportId) : '', {}],
    {
      enabled: !!reportId,
      refetchOnWindowFocus: false,
    },
    false
  );

  const transformed = useMemo(() => {
    if (!query.data || query.isError) return undefined;

    return {
      ...query.data,
      id: String(query.data.id),
      modules: (query.data.modules ?? []).map((m) => transformApiModule(m, definitions ?? undefined)),
    };
  }, [query.data, query.isError, definitions]);

  return { ...query, data: transformed };
};

// ── Templates ──
// The API returns ApiReportTemplate[]; we transform them into frontend
// ReportTemplate objects, inferring platformType from definitions.

export const useGetTemplates = () => {
  const { data: definitions } = useGetModuleDefinitions();

  const query = useQueryWithAccessToken<ApiReportTemplate[]>(
    [queryKeys.templates, {}],
    {
      refetchOnWindowFocus: false,
    },
    false
  );

  const transformed = useMemo(() => {
    if (!query.data) return undefined;

    return query.data.map((t) => transformApiTemplate(t, definitions ?? undefined));
  }, [query.data, definitions]);

  return { ...query, data: transformed };
};

export const useGetTemplate = (templateId: string | null) =>
  useQueryWithAccessToken<ReportTemplate>(
    [templateId ? ReportingUrls.template(templateId) : '', {}],
    {
      enabled: !!templateId,
      refetchOnWindowFocus: false,
    },
    false
  );

// ── Module metrics ──
// Uses GET /v1.0/reports/modules/data with moduleDefinitionId, profileIds, startDate, endDate.
// The data endpoint returns a DynamicModuleData shape (chartType, metrics, data)
// which the DynamicModuleBuilder renders directly based on chartType.

export const useModuleMetrics = (module: ReportModule, profileIds: string[], period: { startDate: string; endDate: string }, enabled = true) => {
  const [{ user }] = useBridgeContext();
  const endpoint = module.moduleDefinitionId ? ReportingUrls.moduleData : '';
  const userOffset = user.timezone;

  const params = {
    profileIds: profileIds.join(','),
    moduleDefinitionId: module.moduleDefinitionId,
    startDate: period.startDate,
    endDate: period.endDate,
    timezoneOffset: userOffset,
  };

  const hasRequiredParams = enabled && !!module.moduleDefinitionId && profileIds.length > 0 && !!period.startDate && !!period.endDate;

  const apiQuery = useQueryWithAccessToken<DynamicModuleData>(
    [endpoint, params],
    {
      enabled: hasRequiredParams,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME_MODULE_METRICS,
    },
    false
  );

  const transformedData = useMemo(() => {
    if (!apiQuery.data) return undefined;

    const rawData = apiQuery.data;
    let hasData = false;

    if (rawData.chartType === 'MetricCard') {
      hasData = (rawData as MetricCardModuleData).data?.current !== undefined;
    } else if (rawData.chartType === 'PostsCardList') {
      hasData = Array.isArray((rawData as PostsCardListModuleData).data) && (rawData as PostsCardListModuleData).data.length > 0;
    } else if (rawData.chartType === 'Line' || rawData.chartType === 'Bar' || rawData.chartType === 'Area') {
      const tsData = rawData as TimeSeriesModuleData;

      hasData = tsData.data?.series != null && tsData.data.series.length > 0;
    } else if (rawData.chartType === 'HeatMap') {
      hasData = Array.isArray((rawData as HeatMapModuleData).data) && (rawData as HeatMapModuleData).data.length > 0;
    } else if (rawData.chartType === 'Scatter') {
      hasData = Array.isArray((rawData as ScatterModuleData).data) && (rawData as ScatterModuleData).data.length > 0;
    } else if (rawData.chartType === 'Pie') {
      hasData = Array.isArray((rawData as PieModuleData).data) && (rawData as PieModuleData).data.length > 0;
    } else if (rawData.chartType === 'SummaryTable') {
      hasData = Array.isArray((rawData as TableModuleData).data) && (rawData as TableModuleData).data.length > 0;
    } else {
      hasData = rawData.data != null && (Array.isArray(rawData.data) ? rawData.data.length > 0 : true);
    }

    return {
      status: hasData ? 'has_data' : 'no_data',
      noDataReason: hasData ? null : ('empty_result' as NoDataReason),
      data: rawData,
      metadata: {
        lastUpdated: apiQuery.dataUpdatedAt ? new Date(apiQuery.dataUpdatedAt).toISOString() : new Date().toISOString(),
        timezone: 'UTC',
        profilesIncluded: profileIds,
        profilesExcluded: [],
      },
    } as ModuleMetricsResponse<DynamicModuleData>;
  }, [apiQuery.data, profileIds]);

  if (!hasRequiredParams) {
    return {
      data: undefined,
      isLoading: false,
      isError: false,
      refetch: () => Promise.resolve(undefined),
    };
  }

  return {
    ...apiQuery,
    data: transformedData,
  };
};

// ── Module metrics (infinite / PostsCardList) ──
// Uses useInfiniteQuery so React Query accumulates all pages natively.
export const useModuleMetricsInfinite = (
  module: ReportModule,
  profileIds: string[],
  period: { startDate: string; endDate: string },
  queryEnabled: boolean
) => {
  const [{ user }] = useBridgeContext();
  const endpoint = module.moduleDefinitionId ? ReportingUrls.moduleData : '';
  const userOffset = user.timezone;

  const params = {
    profileIds: profileIds.join(','),
    moduleDefinitionId: module.moduleDefinitionId,
    startDate: period.startDate,
    endDate: period.endDate,
    timezoneOffset: userOffset,
  };

  const hasRequiredParams = queryEnabled && !!module.moduleDefinitionId && profileIds.length > 0 && !!period.startDate && !!period.endDate;

  const queryResult = useInfiniteQueryWithAccessToken<PostsCardListModuleData>([endpoint, params], {
    enabled: hasRequiredParams,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME_MODULE_METRICS,
    initialPageParam: 1,
    getNextPageParam: (lastPage: PostsCardListModuleData) => (lastPage?.paging?.nextUrl ? lastPage.paging.page + 1 : undefined),
  });

  const mergedData = useMemo((): PostsCardListModuleData | undefined => {
    if (!queryResult.data) return undefined;

    const { pages } = queryResult.data;

    if (pages.length === 0) return undefined;

    const lastPage = pages[pages.length - 1];

    return {
      chartType: 'PostsCardList',
      metrics: lastPage.metrics,
      data: pages.flatMap((p) => p.data),
      paging: lastPage.paging,
    };
  }, [queryResult.data]);

  return {
    mergedData,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    fetchNextPage: queryResult.fetchNextPage,
    hasNextPage: queryResult.hasNextPage ?? false,
  };
};

// ── Mutation hooks ──

/**
 * Helper: builds a custom mutationFn for TW API mutations.
 * The TW .NET API expects a JSON body (camelCase via default CamelCasePropertyNamesPolicy),
 * with access_token as a query param. The default useMutationWithAccessToken puts
 * everything into query params, so we override mutationFn to split auth from body.
 */
const twMutationFn = <TResult, TVars = Record<string, unknown>>(
  method: 'POST' | 'PUT' | 'DELETE',
  url: string,
  mapBody: (vars: Omit<TVars, 'access_token'>) => object
) => {
  return async (variables: TVars): Promise<TResult> => {
    const { access_token, ...data } = variables as TVars & { access_token?: string };

    const response = await fetchWithHeaders({
      method,
      url,
      params: { access_token },
      body: mapBody(data as Omit<TVars, 'access_token'>),
      headers: { 'Content-Type': 'application/json' },
    });

    return unwrapResult<TResult>(response);
  };
};

export const useCreateReport = () => {
  const queryClient = useQueryClient();

  return useMutationWithAccessToken<ApiReport, CreateReportRequest>({
    method: MutationMethodEnum.post,
    endpoint: queryKeys.reports,
    mutationFn: twMutationFn<ApiReport, CreateReportRequest>('POST', queryKeys.reports, (data) => {
      // Report creation supports either template-based creation or custom/blank creation.
      const body: Record<string, unknown> = { workspaceId: data.workspaceId };

      if (data.templateId) {
        body.templateId = data.templateId;
      } else {
        body.name = data.name || 'Untitled Report';
        if (data.period) body.period = data.period;
        if (data.profileIds) body.profileIds = data.profileIds;
        if (data.modules !== undefined) body.modules = data.modules;
      }

      return body;
    }),
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.reports] });
      },
    },
  });
};

export const useDuplicateReport = () => {
  const queryClient = useQueryClient();

  return useMutationWithAccessToken<ApiReport, DuplicateReportRequest>({
    method: MutationMethodEnum.post,
    endpoint: queryKeys.reports,
    mutationFn: async (variables: DuplicateReportRequest & { access_token?: string }) => {
      const { access_token, reportId } = variables;
      const url = ReportingUrls.reportDuplicate(reportId);

      const response = await fetchWithHeaders({
        method: 'POST',
        url,
        params: { access_token },
        body: {},
        headers: { 'Content-Type': 'application/json' },
      });

      return unwrapResult<ApiReport>(response);
    },
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.reports] });
      },
    },
  });
};

export const useUpdateReport = (reportId: string) => {
  const queryClient = useQueryClient();
  const url = ReportingUrls.report(reportId);

  return useMutationWithAccessToken<ApiReport, UpdateReportRequest>({
    method: MutationMethodEnum.put,
    endpoint: url,
    mutationFn: twMutationFn<ApiReport, UpdateReportRequest>('PUT', url, (data) => ({
      name: data.name,
      ...(data.period ? { period: data.period } : {}),
      ...(data.profileIds ? { profileIds: data.profileIds } : {}),
      ...(data.layout ? { layout: data.layout } : {}),
      ...(data.modules ? { modules: data.modules } : {}),
    })),
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [ReportingUrls.report(reportId)] });
        queryClient.invalidateQueries({ queryKey: [queryKeys.reports] });
      },
    },
  });
};

export const useDeleteReport = (reportId: string) => {
  const queryClient = useQueryClient();

  return useMutationWithAccessToken<void, Record<string, never>>({
    method: MutationMethodEnum.delete,
    endpoint: ReportingUrls.report(reportId),
    mutationFn: twMutationFn<void, Record<string, never>>('DELETE', ReportingUrls.report(reportId), () => ({})),
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.reports] });
      },
    },
  });
};

// ── Report profile mutations ──

export const useAddReportProfile = (reportId: string) => {
  const queryClient = useQueryClient();
  const url = ReportingUrls.reportProfiles(reportId);

  return useMutationWithAccessToken<ApiReport, AddProfileRequest>({
    method: MutationMethodEnum.post,
    endpoint: url,
    mutationFn: twMutationFn<ApiReport, AddProfileRequest>('POST', url, (data) => ({
      profileId: data.profileId,
    })),
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [ReportingUrls.report(reportId)] });
      },
    },
  });
};

export const useRemoveReportProfile = (reportId: string) => {
  const queryClient = useQueryClient();

  return useMutationWithAccessToken<void, RemoveProfileRequest>({
    method: MutationMethodEnum.delete,
    endpoint: ReportingUrls.reportProfiles(reportId),
    mutationFn: async (variables: RemoveProfileRequest & { access_token?: string }) => {
      const { access_token, profileId } = variables;
      const url = ReportingUrls.reportProfile(reportId, profileId);

      await fetchWithHeaders({
        method: 'DELETE',
        url,
        params: { access_token },
        headers: { 'Content-Type': 'application/json' },
      });
    },
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [ReportingUrls.report(reportId)] });
      },
    },
  });
};

// ── Report module mutations ──

export const useAddReportModule = (reportId: string) => {
  const queryClient = useQueryClient();
  const url = ReportingUrls.reportModules(reportId);

  return useMutationWithAccessToken<ReportModule, AddModuleRequest>({
    method: MutationMethodEnum.post,
    endpoint: url,
    mutationFn: twMutationFn<ReportModule, AddModuleRequest>('POST', url, (data) => ({
      moduleDefinitionId: data.moduleDefinitionId,
      position: data.position ? { x: data.position.x, y: data.position.y } : undefined,
    })),
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [ReportingUrls.report(reportId)] });
      },
    },
  });
};

export const useUpdateReportModule = (reportId: string, moduleId: string) => {
  const url = ReportingUrls.reportModule(reportId, moduleId);

  return useMutationWithAccessToken<ReportModule, UpdateModuleRequest>({
    method: MutationMethodEnum.put,
    endpoint: url,
    mutationFn: twMutationFn<ReportModule, UpdateModuleRequest>('PUT', url, (data) => ({
      ...(data.displayName !== undefined ? { displayName: data.displayName } : {}),
      ...(data.layout
        ? {
            layout: {
              position: { x: data.layout.position.x, y: data.layout.position.y },
              size: { width: data.layout.size.width, height: data.layout.size.height },
            },
          }
        : {}),
      ...(data.chartType !== undefined ? { chartType: data.chartType } : {}),
    })),
    options: {
      onSuccess: () => {
        // No invalidation needed — optimistic update via context is sufficient
      },
    },
  });
};

export const useRemoveReportModule = (reportId: string, moduleId: string) => {
  const url = ReportingUrls.reportModule(reportId, moduleId);

  return useMutationWithAccessToken<void, Record<string, never>>({
    method: MutationMethodEnum.delete,
    endpoint: url,
    mutationFn: twMutationFn<void, Record<string, never>>('DELETE', url, () => ({})),
    options: {
      onSuccess: () => {
        // No invalidation needed — optimistic update via context is sufficient
      },
    },
  });
};

export const useExportReport = (reportId: string) => {
  const url = `${ReportingUrls.report(reportId)}/export/pdf`;

  return useMutationWithAccessToken<ExportReportResponse, ExportReportRequest>({
    method: MutationMethodEnum.post,
    endpoint: url,
    mutationFn: twMutationFn<ExportReportResponse, ExportReportRequest>('POST', url, (data) => ({
      period: data.period,
      profileIds: data.profileIds,
    })),
  });
};
